import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';

interface Application {
  id: number;
  job_title: string;
  job_company: string;
  employer_name: string;
  cover_letter: string;
  status: 'pending' | 'reviewed' | 'accepted' | 'rejected';
  created_at: string;
}

const JobSeekerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchApplications();
    }
  }, [user]);

  const fetchApplications = async () => {
    try {
      const response = await api.get(`/api/applications/jobseeker/${user?.id}`);
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'reviewed':
        return 'status-reviewed';
      case 'accepted':
        return 'status-accepted';
      case 'rejected':
        return 'status-rejected';
      default:
        return '';
    }
  };

  if (isLoading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Job Seeker Dashboard</h1>
        <p>Welcome, {user?.name}!</p>
        <Link to="/jobs" className="btn btn-primary">
          Browse Jobs
        </Link>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card">
          <h3>Total Applications</h3>
          <p className="stat-number">{applications.length}</p>
        </div>
        <div className="stat-card">
          <h3>Pending</h3>
          <p className="stat-number">
            {applications.filter(app => app.status === 'pending').length}
          </p>
        </div>
        <div className="stat-card">
          <h3>Accepted</h3>
          <p className="stat-number">
            {applications.filter(app => app.status === 'accepted').length}
          </p>
        </div>
      </div>

      <div className="applications-section">
        <h2>Your Applications</h2>
        
        {applications.length === 0 ? (
          <div className="empty-state">
            <p>You haven't applied to any jobs yet.</p>
            <Link to="/jobs" className="btn btn-primary">
              Start Browsing Jobs
            </Link>
          </div>
        ) : (
          <div className="applications-list">
            {applications.map(application => (
              <div key={application.id} className="application-item">
                <div className="application-info">
                  <h3>{application.job_title}</h3>
                  <div className="application-meta">
                    <span className="company">{application.job_company}</span>
                    <span className="employer">Posted by {application.employer_name}</span>
                    <span className={`status ${getStatusColor(application.status)}`}>
                      {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                    </span>
                  </div>
                  <p className="application-date">Applied on {formatDate(application.created_at)}</p>
                  
                  {application.cover_letter && (
                    <div className="cover-letter-preview">
                      <h4>Cover Letter:</h4>
                      <p>{application.cover_letter.substring(0, 200)}...</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobSeekerDashboard;
